import * as React from 'react';
import { debounce } from 'lodash';
import * as qs from 'query-string';
import { updateRating, updateComment, initialValidation } from 'rest/request-feedback';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import type { InjectedProps as BrowserMetricsScreenTransitionStatusProps } from '@atlassian/help-center-common-util/analytics/browsermetrics-screen-transition-status-context';
import { getErrorFromJsonPayload, jsonPayloadPresent } from '@atlassian/help-center-common-util/json-payload-retriever';
import { RequestFeedbackInitialStateWrapper } from './request-feedback-initial-state-wrapper';
import type { PersistedError as InitialError } from 'state/persisted/types';

export interface RequestFeedbackProps {
    portalId: string;
    requestKey: string;
    isLoggedIn: boolean;
    anonymousEnabled: boolean;
    locationSearch: string;
}

interface State {
    isLoading: boolean;
    initialError: InitialError | undefined;
    rating: number | undefined;
    submittedRating: number | undefined;
    commentSubmitting: boolean;
    commentSubmitted: boolean;
    validationErrorType?: string;
}

const SAVE_RATING_BACKEND_CALL_DEBOUNCE_MS = 300;

export class RequestFeedback extends React.Component<
    RequestFeedbackProps & BrowserMetricsScreenTransitionStatusProps & WithAnalyticsEventsProps,
    State
> {
    saveUpdatedRating = debounce(async (newRating: number, previousRating: number | undefined) => {
        const { commentSubmitting } = this.state;
        if (commentSubmitting) {
            return;
        }

        const tokenToUse = this.getTokenFromUrlParams();
        const { portalId, requestKey } = this.props;
        try {
            await updateRating(Number(portalId), requestKey, tokenToUse, newRating);
            this.setState({
                validationErrorType: undefined,
                submittedRating: newRating,
            });
        } catch (errorResponse) {
            this.setState({
                validationErrorType: 'star-rating-submit',
                rating: previousRating,
            });

            trackError(
                'cv.feedback.confirm.page.rating.error',
                {},
                // TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
                `${errorResponse.status} response received from backend when saving updated CSAT rating for portal with id "${portalId}";`
            );
        }
    }, SAVE_RATING_BACKEND_CALL_DEBOUNCE_MS);

    constructor(props: RequestFeedbackProps & BrowserMetricsScreenTransitionStatusProps & WithAnalyticsEventsProps) {
        super(props);
        this.state = {
            isLoading: true,
            initialError: undefined,
            rating: undefined,
            submittedRating: undefined,
            commentSubmitting: false,
            commentSubmitted: false,
            validationErrorType: undefined,
        };
    }

    componentDidMount() {
        this.validateAndInitializeState();
    }

    validateAndInitializeState = () => {
        const token = this.getTokenFromUrlParams();
        if (jsonPayloadPresent()) {
            const initialError = getErrorFromJsonPayload();
            this.setState({
                initialError,
                isLoading: false,
            });
        } else {
            const { requestKey } = this.props;
            // Feedback APIs used by this page does not support tokenless access,
            // As a result customer'd be shown an error page without a token
            initialValidation(requestKey, token).then(
                () => this.setState({ isLoading: false }),
                (errorResponse) => {
                    const initialError = {
                        error: {
                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                            status: errorResponse.status,

                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                            message: errorResponse.response.errorMessage,

                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                            callToActionUrl: errorResponse.response.callToActionUrl,

                            // Suppressing existing violation. Please fix this.
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                            callToActionText: errorResponse.response.callToActionText,
                        },
                    };
                    this.setState({
                        initialError,
                        isLoading: false,
                    });
                }
            );
        }
    };

    getTokenFromUrlParams = (): string | undefined => {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedSearchQuery = qs.parse(this.props.locationSearch);

        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return parsedSearchQuery.token;
    };

    onRatingChange = async (newRating: number) => {
        const previousRating = this.state.submittedRating;
        this.setState({
            rating: newRating,
        });

        await this.saveUpdatedRating(newRating, previousRating);
    };

    onSaveComment = async (newComment: string) => {
        const tokenToUse = this.getTokenFromUrlParams();
        const { portalId, requestKey } = this.props;
        const rating = this.state.rating;
        this.setState({
            commentSubmitting: true,
        });

        this.props.startBrowserMetricsTransitionToNewScreen(ScreenName.FEEDBACK_SUBMITTED);

        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            await updateComment(Number(portalId), requestKey, tokenToUse, rating!, newComment);
            this.setState({
                commentSubmitted: true,
                commentSubmitting: false,
            });
        } catch (errorResponse) {
            this.props.stopBrowserMetricsTransitionToNewScreen();
            this.setState({
                commentSubmitting: false,
                validationErrorType: 'comment-submit',
            });

            trackError(
                'cv.feedback.confirm.page.comment.error',
                {},
                // TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
                `${errorResponse.status} response received from backend when saving CSAT comment for portal with id "${portalId}";`
            );
        }
    };

    onErrorFlagDismiss = () => {
        this.setState({
            validationErrorType: undefined,
        });
    };

    render() {
        const { isLoading, initialError, rating, commentSubmitting, commentSubmitted, validationErrorType } =
            this.state;
        const { anonymousEnabled, isLoggedIn } = this.props;
        return (
            <RequestFeedbackInitialStateWrapper
                isLoading={isLoading}
                error={initialError ? initialError.error : undefined}
                rating={rating || 0}
                // @typescript-eslint upgrade to v5.61.0
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onRatingChange={this.onRatingChange}
                // @typescript-eslint upgrade to v5.61.0
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSaveComment={this.onSaveComment}
                commentSubmitting={commentSubmitting}
                commentSubmitted={commentSubmitted}
                validationErrorType={validationErrorType}
                onErrorFlagDismiss={this.onErrorFlagDismiss}
                redirectToLogin={!anonymousEnabled && !isLoggedIn}
            />
        );
    }
}
